/**
 * ProposalFilters Component
 *
 * Story FOS-4.2.8: ProposalList & Detail Enhancement
 * Task 3: Create ProposalFilters component
 *
 * Filter bar with Active/Pending/Closed/All filter buttons and
 * "Needs my vote" toggle switch.
 *
 * @see AC-4.2.8.1 - When user clicks filter, only matching proposals shown
 * @see AC-4.2.8.3 - "Needs my vote" filter shows proposals needing user's vote
 *
 * @module components/governance/ProposalFilters
 */

// =============================================================================
// Types
// =============================================================================

/**
 * Filter status values matching oracle-bridge expected values (PascalCase).
 * BL-105 / BUG-DAO-017: Fixed from lowercase to match API contract.
 */
export type FilterStatus = 'all' | 'Active' | 'Pending' | 'Passed' | 'Failed';

export interface ProposalFiltersProps {
  /** Current status filter */
  status: FilterStatus;
  /** Whether "needs my vote" filter is active */
  needsMyVote: boolean;
  /** Callback when status filter changes */
  onStatusChange: (status: FilterStatus) => void;
  /** Callback when "needs my vote" toggle changes */
  onNeedsMyVoteChange: (value: boolean) => void;
}

// =============================================================================
// Constants
// =============================================================================

const FILTER_OPTIONS: { value: FilterStatus; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'Active', label: 'Active' },
  { value: 'Pending', label: 'Pending' },
  { value: 'Passed', label: 'Passed' },
  { value: 'Failed', label: 'Failed' },
];

// =============================================================================
// Component
// =============================================================================

/**
 * Filter bar for proposal list
 */
export function ProposalFilters({
  status,
  needsMyVote,
  onStatusChange,
  onNeedsMyVoteChange,
}: ProposalFiltersProps) {
  return (
    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
      {/* Status filter buttons */}
      <div className="flex gap-2" role="group" aria-label="Status filters">
        {FILTER_OPTIONS.map(({ value, label }) => (
          <button
            key={value}
            onClick={() => onStatusChange(value)}
            className={`px-3 py-1.5 text-sm font-medium rounded-md transition-colors ${
              status === value
                ? 'bg-primary text-primary-foreground'
                : 'bg-muted text-muted-foreground hover:bg-muted/80'
            }`}
            data-testid={`filter-btn-${value}`}
            aria-pressed={status === value}
          >
            {label}
          </button>
        ))}
      </div>

      {/* Needs my vote toggle */}
      <label className="flex items-center gap-2 cursor-pointer select-none">
        <input
          type="checkbox"
          checked={needsMyVote}
          onChange={(e) => onNeedsMyVoteChange(e.target.checked)}
          className="w-4 h-4 rounded border-border text-primary focus:ring-primary focus:ring-offset-background"
          data-testid="needs-my-vote-toggle"
        />
        <span className="text-sm text-foreground">Needs my vote</span>
      </label>
    </div>
  );
}

export default ProposalFilters;
